import * as React from "react"
import { cn } from "@/lib/utils"
import { CalendarIcon } from "lucide-react"

export interface DateInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  onValueChange?: (value: string) => void
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      className,
      value,
      onChange,
      onValueChange,
      disabled,
      placeholder,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null)

    const combinedRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        (innerRef as React.MutableRefObject<HTMLInputElement | null>).current = node
        if (typeof ref === "function") ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
      },
      [ref],
    )

    const displayValue = React.useMemo(() => {
      const v = (value ?? "") as string
      if (!v) return ""
      const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (m) return `${m[1]}-${m[2]}-${m[3]}`
      return v
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      onValueChange?.(e.target.value)
    }

    const openPicker = () => {
      if (disabled) return
      innerRef.current?.showPicker?.()
    }

    return (
      <div
        className={cn(
          "relative flex items-center h-10 w-full rounded-md border border-input bg-background text-sm ring-offset-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        {/* 시각적 표시(포맷된 값) — 보조기기에는 숨김, 실제 값은 아래 input이 보유 */}
        <span
          aria-hidden
          className={cn(
            "flex-1 px-3 py-2 select-none truncate pointer-events-none",
            !displayValue && "text-muted-foreground",
          )}
        >
          {displayValue || placeholder || "YYYY-MM-DD"}
        </span>
        <span
          aria-hidden
          className="px-2 py-2 text-muted-foreground shrink-0 pointer-events-none"
        >
          <CalendarIcon className="h-4 w-4" />
        </span>
        {/* 실제 날짜 입력: 키보드 포커스/조작 가능, 스크린리더에 노출. 시각적으로만 투명 처리 */}
        <input
          ref={combinedRef}
          type="date"
          value={(value ?? "") as string}
          onChange={handleChange}
          onClick={openPicker}
          disabled={disabled}
          aria-label={ariaLabel}
          placeholder={placeholder}
          className={cn(
            "absolute inset-0 h-full w-full cursor-pointer opacity-0",
            disabled && "cursor-not-allowed",
          )}
          {...props}
        />
      </div>
    )
  },
)
DateInput.displayName = "DateInput"

export { DateInput }
